// ============================================================================
// ChatAttachmentComposer.tsx — композер вложений чата (SSoT/SRP).
//   Выбор файлов НЕ отправляет сообщение сразу: открывается шторка
//   (мобильный) / модальное окно (десктоп) с превью, комментарием и
//   кнопками «Отправить»/«Отменить». Загрузка — существующий механизм
//   chunkedUpload (чанки, прогресс, durable офлайн-очередь); отправка —
//   chatQueue (sendChatMessage), т.е. офлайн-очередь сообщений.
//   Лимиты и ошибки проверяются ДО загрузки и показываются понятным текстом.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle, File as FileIcon, FileText, Film, Image as ImageIcon,
  Loader2, Music, Paperclip, RefreshCw, Send, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import {
  abortUpload, getUploadJob, getUploadJobs, onUploadsChange, type UploadJob,
} from '@/lib/chunkedUpload';
import {
  ensureUpload, forgetUpload, jobToEntry, type Entry,
} from '@/components/chat/chatAttachmentUploads';
import {
  MAX_CHAT_ATTACHMENT_BYTES, MAX_CHAT_ATTACHMENTS,
} from '@/components/chat/chatAttachmentLimits';
import { fileExt, mediaKind } from '@/lib/fileName';
import { formatBytes } from '@/components/chat/chatInfoData';
import { createLogger } from '@/lib/logger';
import { friendlyError, logSwallowed } from '@/lib/errors';
import { getSDK } from '@/store/sdkStore';

const log = createLogger('ChatAttachmentComposer');

const PREVIEW_LIMIT = 4;

// ---------------------------------------------------------------------------
// Представление элементов (иконка/тип/склонение)
// ---------------------------------------------------------------------------

function typeLabel(name: string, mime: string): string {
  const ext = fileExt(name);
  if (ext) return ext.toUpperCase();
  const sub = (mime || '').split('/')[1];
  if (sub) return sub.replace(/[^a-z0-9]+/gi, ' ').trim().toUpperCase();
  return 'ФАЙЛ';
}

function filesWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'файл';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'файла';
  return 'файлов';
}

function KindIcon({ name, mime }: { name: string; mime: string }) {
  const kind = mediaKind(name, mime);
  const cls = 'h-5 w-5 text-muted-foreground';
  if (kind === 'image') return <ImageIcon className={cls} />;
  if (kind === 'audio') return <Music className={cls} />;
  if (kind === 'video') return <Film className={cls} />;
  if (/\.(pdf|docx?|xlsx?|pptx?|txt|md|csv|rtf)$/i.test(name)) return <FileText className={cls} />;
  return <FileIcon className={cls} />;
}

export interface ChatAttachmentComposerProps {
  /** Выбранные локальные файлы (допустимые после validateChatAttachmentFiles). */
  files: File[];
  /** Предупреждения по отклонённым файлам (показываются в шторке). */
  warnings?: string[];
  /** Уже загруженные вложения (например, голосовые), которые тоже уйдут. */
  extraCount?: number;
  /** Закрыть без отправки (загрузки отменяются, готовые файлы удаляются). */
  onCancel: () => void;
  /** Отправка: комментарий + id загруженных файлов (через chatQueue). */
  onSend: (comment: string, fileIds: string[]) => Promise<void>;
}

export function ChatAttachmentComposer({
  files, warnings = [], extraCount = 0, onCancel, onSend,
}: ChatAttachmentComposerProps) {
  const { isMobile } = useBreakpoint();
  const [entries, setEntries] = useState<Entry[]>(() => files.map((file, index) => ({
    key: `${index}:${file.name}:${file.size}:${file.lastModified}`,
    file,
    name: file.name,
    size: file.size,
    type: file.type,
    status: 'uploading',
    progress: 0,
  })));
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});

  const syncEntry = useCallback((key: string, job: UploadJob | undefined) => {
    if (!job) return;
    setEntries((prev) => {
      let changed = false;
      const next = prev.map((entry) => {
        if (entry.key !== key) return entry;
        const updated = jobToEntry(entry, job);
        if (updated !== entry) changed = true;
        return updated;
      });
      return changed ? next : prev;
    });
  }, []);

  const startEntry = useCallback((entry: Entry) => {
    const record = ensureUpload(entry.file);
    const jobId = record.jobId || getUploadJobs().find((j) => j.blob === entry.file)?.id || '';
    if (!jobId) {
      setEntries((prev) => prev.map((e) => (
        e.key === entry.key ? { ...e, status: 'error', error: 'Не удалось начать загрузку' } : e
      )));
      return;
    }
    setEntries((prev) => prev.map((e) => (e.key === entry.key ? { ...e, jobId } : e)));
    syncEntry(entry.key, getUploadJob(jobId));
    void record.promise
      .then((job) => syncEntry(entry.key, job))
      .catch((err) => {
        log.warn('attachment upload failed', err);
        setEntries((prev) => prev.map((e) => (
          e.key === entry.key ? { ...e, status: 'error', error: 'Не удалось загрузить файл' } : e
        )));
      });
  }, [syncEntry]);

  // Запуск передач один раз при открытии композера.
  useEffect(() => {
    for (const entry of entries) {
      if (entry.status === 'uploading') startEntry(entry);
    }
    // entries — исходный набор на момент монтирования; дальнейшие изменения
    // отслеживаются подпиской onUploadsChange (id передач не меняются).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Прогресс/статусы — из единого менеджера передач (включая офлайн-очередь:
  // после восстановления связи передача продолжается с тем же id).
  useEffect(() => onUploadsChange(() => {
    setEntries((prev) => {
      let changed = false;
      const next = prev.map((entry) => {
        if (!entry.jobId) return entry;
        const job = getUploadJob(entry.jobId);
        if (!job) return entry;
        const updated = jobToEntry(entry, job);
        if (updated !== entry) changed = true;
        return updated;
      });
      return changed ? next : prev;
    });
  }), []);

  // Превью изображений (blob URL; освобождаются при закрытии композера).
  useEffect(() => {
    const map: Record<string, string> = {};
    for (const entry of entries) {
      if (mediaKind(entry.name, entry.type) !== 'image') continue;
      try {
        map[entry.key] = URL.createObjectURL(entry.file);
      } catch (err) {
        logSwallowed(err, 'components/chat/ChatAttachmentComposer.tsx:thumb');
      }
    }
    setThumbs(map);
    return () => {
      for (const url of Object.values(map)) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeEntry = useCallback((entry: Entry) => {
    if (sending) return;
    forgetUpload(entry.file);
    // Оптимистично убираем карточку; загрузку/файл чистим в фоне.
    const remaining = entries.filter((e) => e.key !== entry.key);
    setEntries(remaining);
    if (remaining.length === 0) onCancel();
    void (async () => {
      try {
        if (entry.status === 'ready' && entry.fileId) {
          await getSDK().files.deleteFile({ id: entry.fileId });
        } else if (entry.jobId) {
          await abortUpload(entry.jobId);
        }
      } catch (err) {
        logSwallowed(err, 'components/chat/ChatAttachmentComposer.tsx:remove');
      }
    })();
  }, [entries, onCancel, sending]);

  const cancel = useCallback(async () => {
    if (sending) return;
    await Promise.all(entries.map(async (entry) => {
      forgetUpload(entry.file);
      try {
        if (entry.status === 'ready' && entry.fileId) {
          await getSDK().files.deleteFile({ id: entry.fileId });
        } else if (entry.jobId) {
          await abortUpload(entry.jobId);
        }
      } catch (err) {
        logSwallowed(err, 'components/chat/ChatAttachmentComposer.tsx:cancel');
      }
    }));
    onCancel();
  }, [entries, onCancel, sending]);

  const retry = useCallback((entry: Entry) => {
    if (sending) return;
    forgetUpload(entry.file);
    setEntries((prev) => prev.map((e) => (
      e.key === entry.key
        ? { ...e, status: 'uploading', progress: 0, error: undefined, fileId: undefined, jobId: undefined }
        : e
    )));
    startEntry({ ...entry, status: 'uploading', progress: 0, error: undefined, fileId: undefined, jobId: undefined });
  }, [sending, startEntry]);

  // Esc — отмена (как в остальных окнах платформы).
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      ev.preventDefault();
      void cancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cancel]);

  const fileIds = entries.map((e) => e.fileId).filter((id): id is string => !!id);
  const allReady = entries.length > 0 && entries.every((e) => e.status === 'ready' && !!e.fileId);
  const uploadingCount = entries.filter((e) => e.status === 'uploading').length;
  const queuedCount = entries.filter((e) => e.status === 'queued').length;
  const errorCount = entries.filter((e) => e.status === 'error').length;
  const preview = expanded ? entries : entries.slice(0, PREVIEW_LIMIT);
  const hidden = Math.max(0, entries.length - PREVIEW_LIMIT);

  const handleSend = async () => {
    if (!allReady || sending) return;
    setSending(true);
    setError(null);
    try {
      await onSend(comment.trim(), fileIds);
    } catch (err) {
      setError(friendlyError(err));
      setSending(false);
    }
  };

  const renderEntry = (entry: Entry) => {
    const thumb = thumbs[entry.key];
    return (
      <div
        key={entry.key}
        className="relative flex min-w-0 items-start gap-2 rounded-xl border border-border/70 bg-card/60 p-2"
      >
        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border/50 bg-muted/40">
          {thumb
            ? <img src={thumb} alt="" className="h-full w-full object-cover" />
            : <KindIcon name={entry.name} mime={entry.type} />}
        </div>
        <div className="min-w-0 flex-1 pr-5">
          <div className="truncate text-[12px] font-medium" title={entry.name}>{entry.name}</div>
          <div className="mt-0.5 text-[10px] text-muted-foreground">
            {formatBytes(entry.size) || '0 Б'} · {typeLabel(entry.name, entry.type)}
          </div>
          {entry.status === 'uploading' && (
            <>
              <div className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                {Math.round(entry.progress * 100)}%
              </div>
              <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-[width] duration-200"
                  style={{ width: `${Math.max(2, Math.round(entry.progress * 100))}%` }}
                />
              </div>
            </>
          )}
          {entry.status === 'queued' && (
            <div className="mt-1 text-[10px] text-amber-600 dark:text-amber-400">Ожидает сети</div>
          )}
          {entry.status === 'ready' && (
            <div className="mt-1 text-[10px] text-emerald-600 dark:text-emerald-400">Готово</div>
          )}
          {entry.status === 'error' && (
            <div className="mt-1 flex items-center gap-1 text-[10px] text-destructive">
              <AlertCircle className="h-3 w-3 shrink-0" />
              <span className="min-w-0 flex-1 truncate" title={entry.error}>{entry.error || 'Ошибка загрузки'}</span>
              <button
                type="button"
                className="flex shrink-0 items-center gap-0.5 underline hover:no-underline"
                onClick={() => retry(entry)}
                disabled={sending}
              >
                <RefreshCw className="h-3 w-3" /> Повторить
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          className="absolute right-1 top-1 rounded-full p-1 text-muted-foreground hover:bg-muted/60"
          title="Убрать файл"
          aria-label={`Убрать файл ${entry.name}`}
          onClick={() => void removeEntry(entry)}
          disabled={sending}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-[12000] flex justify-center bg-black/50 backdrop-blur-[2px] ${isMobile ? 'items-end' : 'items-center p-4'}`}
      role="dialog"
      aria-modal="true"
      aria-label="Отправка вложений"
      onClick={(e) => { if (e.target === e.currentTarget) void cancel(); }}
    >
      <div
        className={`flex min-h-0 w-full flex-col border border-border/70 bg-background shadow-2xl ${
          isMobile
            ? 'max-h-[88dvh] rounded-t-2xl pb-[env(safe-area-inset-bottom)]'
            : 'max-h-[min(82vh,760px)] w-[min(580px,94vw)] rounded-2xl'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {isMobile && <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-muted-foreground/30" />}

        <div className="flex shrink-0 items-start gap-2 px-4 pb-2 pt-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Paperclip className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">Вложения</div>
            <div className="text-[11px] text-muted-foreground">
              {entries.length} {filesWord(entries.length)}
              {extraCount > 0 ? ` + ${extraCount} уже загружено` : ''}
            </div>
          </div>
          <button
            type="button"
            className="rounded-full p-1.5 text-muted-foreground hover:bg-muted/60"
            aria-label="Закрыть"
            onClick={() => void cancel()}
            disabled={sending}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 pb-2">
          {warnings.length > 0 && (
            <div className="flex items-start gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-2 text-[11px] text-amber-700 dark:text-amber-300">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <div className="space-y-0.5">
                {warnings.map((w) => <div key={w}>{w}</div>)}
              </div>
            </div>
          )}

          {entries.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border/70 p-4 text-center text-[12px] text-muted-foreground">
              Все файлы убраны
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {preview.map(renderEntry)}
              {!expanded && hidden > 0 && (
                <button
                  type="button"
                  className="flex min-h-[68px] flex-col items-center justify-center gap-0.5 rounded-xl border border-dashed border-border/70 bg-muted/20 text-[12px] font-medium text-muted-foreground hover:bg-muted/40"
                  onClick={() => setExpanded(true)}
                >
                  <span className="text-base font-semibold">+{hidden}</span>
                  <span>ещё {filesWord(hidden)}</span>
                </button>
              )}
            </div>
          )}

          {expanded && hidden > 0 && (
            <button
              type="button"
              className="w-full rounded-lg border border-border/60 py-1 text-[11px] text-muted-foreground hover:bg-muted/40"
              onClick={() => setExpanded(false)}
            >
              Свернуть список
            </button>
          )}

          {queuedCount > 0 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-2 text-[11px] text-amber-700 dark:text-amber-300">
              Нет сети: {queuedCount} {filesWord(queuedCount)} в очереди загрузки. Отправка станет
              доступна, когда файлы загрузятся после восстановления связи.
            </div>
          )}
          {errorCount > 0 && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-2.5 py-2 text-[11px] text-destructive">
              Не загрузились файлы: {errorCount}. Нажмите «Повторить» у файла или уберите его.
            </div>
          )}
        </div>

        <div className="shrink-0 space-y-2 border-t border-border/70 px-4 py-3">
          <label htmlFor="chat-attachment-comment" className="block text-[11px] font-medium text-muted-foreground">
            Комментарий (необязательно)
          </label>
          <textarea
            id="chat-attachment-comment"
            className="max-h-32 min-h-[64px] w-full resize-y rounded-lg border border-border/70 bg-background px-2.5 py-2 text-[13px] leading-snug outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            placeholder="Подпись к вложениям…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={sending}
            autoFocus={!isMobile}
          />

          {error && (
            <div className="flex items-start gap-1.5 rounded-lg border border-destructive/30 bg-destructive/10 px-2.5 py-2 text-[11px] text-destructive">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            <span className="min-w-0 truncate text-[10px] text-muted-foreground">
              До {MAX_CHAT_ATTACHMENTS} {filesWord(MAX_CHAT_ATTACHMENTS)} · до {formatBytes(MAX_CHAT_ATTACHMENT_BYTES)} каждый
            </span>
            <div className="flex shrink-0 gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => void cancel()} disabled={sending}>
                Отменить
              </Button>
              <Button type="button" size="sm" onClick={() => void handleSend()} disabled={!allReady || sending}>
                {sending
                  ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />Отправка…</>
                  : allReady || (uploadingCount === 0 && queuedCount === 0)
                    ? <><Send className="mr-1 h-3.5 w-3.5" />Отправить</>
                    : <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />Загрузка…</>}
              </Button>
            </div>
          </div>
          {!allReady && !sending && (uploadingCount > 0 || queuedCount > 0) && (
            <div className="text-right text-[10px] text-muted-foreground">
              Готово {entries.length - uploadingCount - queuedCount - errorCount} из {entries.length}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
